// @flow
import * as React from 'react';
import { ClientModerationCard, ResponseTextBox, ButtonGroup } from 'components';
import { type CoreDatum } from 'ducks/coreDataReducer';
import { SimpleResponse } from 'components/ContentSection/ReviewResponse';
import {
  updateObservations,
  updateSiteStatus,
  requestObservationsUpdate,
  requestAddResponse,
  requestDeleteResponse,
  requestEditResponse,
  updateUgc,
} from 'ducks/coreDataActions';
import { bindActionCreators } from 'redux';
import { Review, UGC } from 'models';
import classnames from 'classnames';
import { ClientContext } from 'containers/client/ClientProvider';
import { AuthContext } from 'ducks/auth-context';
import UgcAssignmentContainer from 'containers/client/UgcAssignmentContainer';
import UgcSiteStatus from 'containers/client/UgcSiteStatus';
import { UgcFeaturedMedia } from 'containers/client/UgcFeaturedMedia';
import { Map } from 'immutable';
import {
  QuestionSection,
  AnswerSection,
  AddAnswer,
} from 'components/ContentSection/__future__';

type Props = {
  datum: CoreDatum,
  clientId: Id,
  role: UserRole,
  dispatch: Function,
};

type State = {
  pendingResponse: string,
  isEditingResponse: boolean,
};

const defaultLocale = 'en_US';

export default class ClientCard extends React.Component<Props, State> {
  state = {
    pendingResponse: '',
    isEditingResponse: false,
  };

  actions: * = bindActionCreators(
    {
      updateObservations,
      updateSiteStatus,
      requestObservationsUpdate,
      requestAddResponse,
      requestDeleteResponse,
      requestEditResponse,
    },
    this.props.dispatch
  );

  addResponse = () => {
    const ugc = this.props.datum.ugc;
    const subject = this.props.datum.subject;
    const isEdittingMerchantResponse =
      ugc.ugc_archetype === 'Review' && this.state.isEditingResponse;

    const addPayload = {
      parent_ugc_id: ugc.ugc_id,
      subject_id: subject.subject_id,
      catalog_id: subject.merchant.id,
      text: this.state.pendingResponse,
      ugc_type: ugc.ugc_archetype === 'Review' ? 'MerchantResponse' : 'Answer',
      locale: defaultLocale,
    };

    if (isEdittingMerchantResponse) {
      // $FlowFixMe
      const merchantResponse = UGC.getMerchantResponse(ugc);
      const editPayload = {
        ...addPayload,
        ugc_id: merchantResponse.ugc_id,
      };
      this.actions.requestEditResponse(editPayload, ugc.ugc_id);
    } else {
      this.actions.requestAddResponse(addPayload, ugc.ugc_id);
    }
  };

  editMerchantResponse = () => {
    // $FlowFixMe
    const ugc: Review = this.props.datum.ugc;
    const merchantResponse = UGC.getMerchantResponse(ugc);

    this.setState({
      isEditingResponse: true,
      pendingResponse: merchantResponse.text,
    });
  };

  deleteMerchantResponse = () => {
    // $FlowFixMe
    const ugc: Review = this.props.datum.ugc;
    const merchantResponse = UGC.getMerchantResponse(ugc);

    global.confirm('Delete Response?') &&
      this.actions.requestDeleteResponse(merchantResponse.ugc_id, ugc.ugc_id);
  };

  handlePendingResponseChange = (value: string) => {
    this.setState(prevState => ({
      pendingResponse: value,
      isEditingResponse:
        prevState.isEditingResponse && !value
          ? false
          : prevState.isEditingResponse,
    }));
  };

  getTextBoxProps = () => {
    const { responseRequestStatus } = this.props.datum;

    return {
      onSubmit: this.addResponse,
      onChange: this.handlePendingResponseChange,
      value: this.state.pendingResponse,
      status: responseRequestStatus,
    };
  };

  renderTextBox = () => {
    const {
      ugc,
      ugc: { ugc_archetype },
    } = this.props.datum;
    const { isEditingResponse } = this.state;
    let el = null;

    if (ugc_archetype === 'Review') {
      // render textbox is there is no merchant response
      // or the current is being editted
      // $FlowFixMe
      const reviewHasNoMerchantResponse = !UGC.getMerchantResponse(ugc);
      const isUpdatedReview = ugc.old_ugc_id !== null ? true : false;
      const shouldEnableMerchantResponse =
        (isEditingResponse || reviewHasNoMerchantResponse) && !isUpdatedReview;

      el = shouldEnableMerchantResponse ? (
        <MerchantResponseTextBox {...this.getTextBoxProps()} />
      ) : null;
    }

    return el ? <div className="ma4">{el}</div> : el;
  };

  componentWillUpdate(nextProps: Props) {
    // clear responseStates after success status
    const {
      deleteResponseRequestStatus,
      responseRequestStatus,
    } = this.props.datum;
    const {
      deleteResponseRequestStatus: nextDeleteResponseRequestStatus,
      responseRequestStatus: nextResponseRequestStatus,
    } = nextProps.datum;

    // clear pendingResponse after new response is added
    if (
      responseRequestStatus !== nextResponseRequestStatus &&
      responseRequestStatus &&
      !nextResponseRequestStatus
    ) {
      this.setState({
        pendingResponse: '',
        isEditingResponse: false,
      });
    }

    if (
      deleteResponseRequestStatus !== nextDeleteResponseRequestStatus &&
      deleteResponseRequestStatus &&
      !nextDeleteResponseRequestStatus
    ) {
      //this.setState()
    }
  }

  beforeMetaContent(datum: CoreDatum, isEditingResponse: boolean) {
    const { ugc, deleteResponseRequestStatus } = datum;

    const fragments = [];

    if (ugc instanceof Review) {
      const brandResponse = ugc.getBrandResponse();
      const merchantResponse = ugc.getMerchantResponse();
      const showMerchantResponse = isEditingResponse === false;

      if (merchantResponse && showMerchantResponse) {
        fragments.push(
          <div
            className="flex pa4"
            key="merchant-response"
            style={{
              opacity: deleteResponseRequestStatus === 'pending' ? 0.3 : 1,
            }}
          >
            <div className="flex-auto">
              {deleteResponseRequestStatus instanceof Error && (
                <div
                  className="tc ph2 pv1 f6 mv1 dib br1"
                  style={{
                    color: '#C23030',
                    backgroundColor: '#FFDDDD',
                  }}
                >
                  Could not delete response
                </div>
              )}
              <SimpleResponse ugc={merchantResponse} />
            </div>
            <div className="flex-none">
              <ButtonGroup>
                <EditResponseButton onClick={this.editMerchantResponse}>
                  Edit
                </EditResponseButton>

                <EditResponseButton onClick={this.deleteMerchantResponse}>
                  Delete
                </EditResponseButton>
              </ButtonGroup>
            </div>
          </div>
        );
      }

      if (brandResponse) {
        fragments.push(
          <div className="pa4">
            <SimpleResponse key="brand-response" ugc={brandResponse} />
          </div>
        );
      }
    }

    return fragments.length ? fragments : null;
  }

  render() {
    const {
      ugc,
      moderationState,
      subject,
      observationRequestStatus: submitStatus,
      pendingSiteStatus,
    } = this.props.datum;
    const ENABLE_UGC_EDITING = 'ENABLE_UGC_EDITING';

    return (
      <AuthContext.Consumer>
        {authState => {
          return (
            <ClientContext.Consumer>
              {({ clientId, userProfiles, expertUserProfiles, properties }) => {
                const isNativeContent = subject.merchant.group_id === clientId;
                const userProfilesForAssignment = (() => {
                  if (ugc.ugctype === 'Review') return userProfiles;
                  if (ugc.ugctype === 'Question') return expertUserProfiles;

                  return undefined;
                })();
                const showUgcAssignment = (() => {
                  if (!Array.isArray(userProfilesForAssignment)) return false;

                  if (ugc.ugctype === 'Review') {
                    return isNativeContent;
                  } else if (ugc.ugctype === 'Question') {
                    return authState.isQAEnabled() && isNativeContent;
                  }

                  return false;
                })();
                const allowMedia = Map.isMap(properties)
                  ? properties.get('ENABLE_VISUAL_ANSWERS', false)
                  : false;
                const isMedia =
                  ugc.ugctype === 'Image' || ugc.ugctype === 'Video';
                const enableFeaturedMedia = Map.isMap(properties)
                  ? properties.get('ENABLE_MEDIA_FEATURED_FLAG', false)
                  : false;
                const isFeaturedMedia = isMedia && ugc.featured === true;

                return (
                  <ClientModerationCard
                    product={subject}
                    moderationState={moderationState}
                    ugc={ugc}
                    clientId={clientId}
                    onChange={this.actions.updateObservations}
                    onSiteStatusChange={this.actions.updateSiteStatus}
                    status={submitStatus}
                    onSubmit={this.actions.requestObservationsUpdate}
                    authToken={authState.getToken()}
                    shouldEnableUgcEditing={
                      Map.isMap(properties)
                        ? properties.get(ENABLE_UGC_EDITING, false)
                        : false
                    }
                  >
                    {ugc.ugc_archetype === 'Answer' && (
                      <ClientModerationCard.ContentSection>
                        <AnswerSection
                          ugc={ugc}
                          subject={subject}
                          locale={defaultLocale}
                          allowMedia={allowMedia}
                        />
                      </ClientModerationCard.ContentSection>
                    )}
                    {ugc.ugc_archetype === 'Question' && (
                      <ClientModerationCard.ContentSection>
                        <QuestionSection
                          ugc={ugc}
                          subject={subject}
                          locale={defaultLocale}
                          allowMedia={allowMedia}
                        >
                          <AddAnswer
                            questionId={ugc.ugc_id}
                            subject={subject}
                            locale={defaultLocale}
                            allowMedia={allowMedia}
                            onSubmit={newAnswer =>
                              this.props.dispatch(
                                updateUgc(ugc => {
                                  return ugc.updateIn(
                                    ['ugc_related_content'],
                                    v => v.push(newAnswer)
                                  );
                                }, ugc.ugc_id)
                              )
                            }
                          />
                        </QuestionSection>
                      </ClientModerationCard.ContentSection>
                    )}
                    <ClientModerationCard.Aside>
                      <UgcSiteStatus
                        published={pendingSiteStatus || ugc.published}
                        onChange={desiredPublished =>
                          this.actions.updateSiteStatus(
                            {
                              moderationState,
                              desiredPublished,
                            },
                            {
                              clientId,
                              ugctype: ugc.ugctype,
                              ugcClientId: subject.merchant.group_id,
                            }
                          )
                        }
                      />
                      {isMedia &&
                        enableFeaturedMedia && (
                          <UgcFeaturedMedia
                            ugc_id={ugc.ugc_id}
                            ugc_type={ugc.ugctype}
                            featured={isFeaturedMedia}
                            onSubmit={res =>
                              this.props.dispatch(updateUgc(res, ugc.ugc_id))
                            }
                          />
                        )}
                    </ClientModerationCard.Aside>
                    {showUgcAssignment && (
                      <ClientModerationCard.Aside label="Assign to a Team member">
                        <UgcAssignmentContainer
                          userProfiles={userProfilesForAssignment}
                          ugc={ugc}
                          clientId={clientId}
                        />
                      </ClientModerationCard.Aside>
                    )}

                    <ClientModerationCard.BeforeMeta>
                      {this.beforeMetaContent(
                        this.props.datum,
                        this.state.isEditingResponse
                      )}
                    </ClientModerationCard.BeforeMeta>
                    <ClientModerationCard.AfterMeta>
                      {this.renderTextBox()}
                    </ClientModerationCard.AfterMeta>
                  </ClientModerationCard>
                );
              }}
            </ClientContext.Consumer>
          );
        }}
      </AuthContext.Consumer>
    );
  }
}

function MerchantResponseTextBox(props) {
  return (
    <ResponseTextBox
      placeholder="Add a response to this review..."
      buttonLabel="Add Response"
      {...props}
    />
  );
}

function EditResponseButton(props) {
  const className = classnames(
    'button-reset bg-animate pointer br1 blue hover-dark-blue ph3 pv2 lh-solid hover-shadow bg-white'
  );
  return <button type="button" className={className} {...props} />;
}



// WEBPACK FOOTER //
// ./src/containers/ClientCard.js